/**
 * Карточка задачи доски: кто взял и «Взять / Отпустить», правка полей с проверкой версии (задачу поменяли, пока вы
 * правили, — отказ и свежие данные, а не затирание), комментарии и поручения чатам.
 */
import {
  BOARD_NOTE_KIND_TITLES,
  BOARD_NOTE_KINDS,
  BOARD_PRIO_HINTS,
  BOARD_PRIOS,
  BOARD_STATUS_TITLES,
  BOARD_STATUSES,
  type BoardMe,
  type BoardMember,
  type BoardNoteKind,
  type BoardPrio,
  type BoardStatus,
  type BoardTaskDetail,
  type BoardTaskPatch,
} from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Sheet } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { BOARD_API } from './access';
import { PRIO_TONE, StatusBadge, takerText, useBoardError, useRefreshBoard } from './shared';
import { canRelease, canTake, useTakeRelease } from './TasksTab';

interface Draft {
  title: string;
  chat: string;
  prio: BoardPrio;
  rank: string;
  status: BoardStatus;
  leadUserId: string;
  reqs: string;
  branch: string;
  blocker: string;
  details: string;
}

const draftOf = (t: BoardTaskDetail): Draft => ({
  title: t.title,
  chat: t.chat ?? '',
  prio: t.prio,
  rank: String(t.rank),
  status: t.status,
  leadUserId: t.leadUserId ?? '',
  reqs: t.reqs.join(', '),
  branch: t.branch ?? '',
  blocker: t.blocker ?? '',
  details: t.details ?? '',
});

/** Только изменённые поля: правка не трогает то, что человек не менял */
function patchOf(t: BoardTaskDetail, d: Draft): BoardTaskPatch {
  const p: BoardTaskPatch = { version: t.version };
  const reqs = d.reqs
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (d.title.trim() !== t.title) p.title = d.title.trim();
  if ((d.chat.trim() || null) !== t.chat) p.chat = d.chat.trim() || null;
  if (d.prio !== t.prio) p.prio = d.prio;
  if (Number(d.rank) !== t.rank && Number.isInteger(Number(d.rank))) p.rank = Number(d.rank);
  if (d.status !== t.status) p.status = d.status;
  if ((d.leadUserId || null) !== t.leadUserId) p.leadUserId = d.leadUserId || null;
  if (reqs.join(',') !== t.reqs.join(',')) p.reqs = reqs;
  if ((d.branch.trim() || null) !== t.branch) p.branch = d.branch.trim() || null;
  if ((d.blocker.trim() || null) !== t.blocker) p.blocker = d.blocker.trim() || null;
  if ((d.details.trim() || null) !== t.details) p.details = d.details.trim() || null;
  return p;
}

function Notes({ task }: { task: BoardTaskDetail }) {
  const refresh = useRefreshBoard();
  const [kind, setKind] = useState<BoardNoteKind>('comment');
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api(`${BOARD_API}/tasks/${task.id}/notes`, { method: 'POST', body: { kind, text: text.trim() } }),
    onSuccess: () => {
      setText('');
      toast(kind === 'instruction' ? 'Поручение записано' : 'Комментарий записан');
      refresh();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (text.trim()) add.mutate();
  }
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-[15px] font-semibold text-ink">Комментарии и поручения</h3>
      {task.notes.length ? (
        <ul className="flex flex-col gap-2">
          {task.notes.map((n) => (
            <li key={n.id} className="rounded-md border border-line bg-surface-2 px-3 py-2">
              <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-ink-2">
                {n.kind === 'instruction' ? <Badge tone="accent">поручение</Badge> : null}
                <span className="font-medium text-ink">
                  {n.authorChat ? `чат ${n.authorChat}` : (n.authorName ?? '—')}
                </span>
                {n.authorChat && n.authorName ? <span>(ключ: {n.authorName})</span> : null}
                <span>· {dateTime(n.createdAt)}</span>
              </div>
              <p className="mt-1 text-[14px] leading-5 [overflow-wrap:anywhere] whitespace-pre-wrap text-ink">
                {n.text}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">Пока ничего.</p>
      )}
      <form onSubmit={submit} className="flex flex-col gap-2" noValidate>
        <Field label="Вид" htmlFor="note-kind">
          <Select id="note-kind" value={kind} onChange={(e) => setKind(e.target.value as BoardNoteKind)}>
            {BOARD_NOTE_KINDS.map((k) => (
              <option key={k} value={k}>
                {BOARD_NOTE_KIND_TITLES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Текст" htmlFor="note-text">
          <Textarea
            id="note-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={20_000}
          />
        </Field>
        <div>
          <Button type="submit" loading={add.isPending} disabled={!text.trim()}>
            Добавить {kind === 'instruction' ? 'поручение' : 'комментарий'}
          </Button>
        </div>
        <ErrorBox error={add.error} />
      </form>
    </section>
  );
}

export function TaskSheet({ id, me, onClose }: { id: string | undefined; me: BoardMe; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['devboard', 'task', id],
    queryFn: () => api<BoardTaskDetail>(`${BOARD_API}/tasks/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
  const members = useQuery({
    queryKey: ['devboard', 'members'],
    queryFn: () => api<BoardMember[]>(`${BOARD_API}/members`),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  });
  const task = q.data;
  // Форма правится от «основы» — версии, которую человек открыл; свежие данные с сервера (чат взял задачу, кто-то
  // добавил комментарий) приходят в форму, только пока в ней нет несохранённых правок
  const [base, setBase] = useState<BoardTaskDetail | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const patch = base && draft ? patchOf(base, draft) : null;
  const changed = patch ? Object.keys(patch).length > 1 : false;
  const reset = (t: BoardTaskDetail) => {
    setBase(t);
    setDraft(draftOf(t));
  };
  useEffect(() => {
    if (!task) return;
    if (!base || base.id !== task.id || (!changed && base.version !== task.version)) {
      setBase(task);
      setDraft(draftOf(task));
    }
  }, [task, base, changed]);
  const refresh = useRefreshBoard();
  const onError = useBoardError();
  const { take, release } = useTakeRelease();
  const save = useMutation({
    mutationFn: (p: BoardTaskPatch) =>
      api<BoardTaskDetail>(`${BOARD_API}/tasks/${base!.id}`, { method: 'PATCH', body: p }),
    onSuccess: (t) => {
      reset(t);
      toast('Задача сохранена');
      refresh();
    },
    onError,
  });
  const set = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const outdated = Boolean(task && base && changed && base.version !== task.version);

  return (
    <Sheet
      open={Boolean(id)}
      onOpenChange={(v) => !v && onClose()}
      title={task?.title ?? 'Задача'}
      description={
        task
          ? `${task.prio} · ${task.chat ? `чат ${task.chat}` : 'без чата'}${task.ref ? ` · ${task.ref}` : ''}`
          : undefined
      }
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : task && base && draft ? (
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-2 rounded-md border border-line px-3 py-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone={PRIO_TONE[task.prio]}>{task.prio}</Badge>
              <StatusBadge status={task.status} />
            </div>
            <p className="text-[14px] text-ink">{takerText(task)}</p>
            <div className="flex flex-wrap gap-2">
              {canTake(task) ? (
                <Button loading={take.isPending} onClick={() => take.mutate(task.id)}>
                  Взять
                </Button>
              ) : null}
              {canRelease(task, me) ? (
                <Button variant="ghost" loading={release.isPending} onClick={() => release.mutate(task.id)}>
                  Отпустить
                </Button>
              ) : null}
            </div>
          </section>

          <form
            className="flex flex-col gap-3"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (patch && changed) save.mutate(patch);
            }}
          >
            <Field label="Что сделать" htmlFor="task-title" error={fieldErrors.title}>
              <Input
                id="task-title"
                value={draft.title}
                maxLength={500}
                onChange={(e) => set({ title: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Чат" htmlFor="task-chat" hint="Код: plan, site, main…" error={fieldErrors.chat}>
                <Input
                  id="task-chat"
                  value={draft.chat}
                  maxLength={32}
                  autoCapitalize="none"
                  onChange={(e) => set({ chat: e.target.value.toLowerCase() })}
                />
              </Field>
              <Field label="Статус" htmlFor="task-status">
                <Select
                  id="task-status"
                  value={draft.status}
                  onChange={(e) => set({ status: e.target.value as BoardStatus })}
                >
                  {BOARD_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {BOARD_STATUS_TITLES[s]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Приоритет" htmlFor="task-prio" hint={BOARD_PRIO_HINTS[draft.prio]}>
                <Select
                  id="task-prio"
                  value={draft.prio}
                  onChange={(e) => set({ prio: e.target.value as BoardPrio })}
                >
                  {BOARD_PRIOS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Порядок в приоритете" htmlFor="task-rank" hint="Меньше — выше">
                <Input
                  id="task-rank"
                  inputMode="numeric"
                  value={draft.rank}
                  onChange={(e) => set({ rank: e.target.value.replace(/[^\d-]/g, '') })}
                />
              </Field>
              <Field label="Ведёт со стороны компании" htmlFor="task-lead">
                <Select
                  id="task-lead"
                  value={draft.leadUserId}
                  onChange={(e) => set({ leadUserId: e.target.value })}
                >
                  <option value="">—</option>
                  {(members.data ?? []).map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.fullName}
                    </option>
                  ))}
                  {task.leadUserId && !members.data?.some((m) => m.userId === task.leadUserId) ? (
                    <option value={task.leadUserId}>{task.leadName ?? 'человек вне доски'}</option>
                  ) : null}
                </Select>
              </Field>
              <Field label="Ветка" htmlFor="task-branch" hint="ready/chat-… и коммит">
                <Input
                  id="task-branch"
                  value={draft.branch}
                  maxLength={200}
                  onChange={(e) => set({ branch: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Требования ТЗ" htmlFor="task-reqs" hint="Через запятую: ПЛ-28, ДС-05">
              <Input id="task-reqs" value={draft.reqs} onChange={(e) => set({ reqs: e.target.value })} />
            </Field>
            <Field label="Что мешает" htmlFor="task-blocker">
              <Input
                id="task-blocker"
                value={draft.blocker}
                maxLength={2000}
                onChange={(e) => set({ blocker: e.target.value })}
              />
            </Field>
            <Field label="Подробности" htmlFor="task-details">
              <Textarea
                id="task-details"
                value={draft.details}
                maxLength={20_000}
                className="min-h-40"
                onChange={(e) => set({ details: e.target.value })}
              />
            </Field>
            {outdated ? (
              <p role="status" className="rounded-md bg-warning-soft px-3 py-2 text-[13px] text-warning">
                Пока вы правили, задачу изменили (версия {task.version}). «Вернуть как было» покажет свежую
                версию — ваши правки придётся внести заново.
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" variant="primary" loading={save.isPending} disabled={!changed}>
                Сохранить
              </Button>
              {changed ? (
                <Button variant="ghost" onClick={() => reset(task)}>
                  Вернуть как было
                </Button>
              ) : null}
            </div>
          </form>

          <Notes task={task} />

          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 border-t border-line pt-3 text-[12px] text-ink-2 sm:grid-cols-[max-content_1fr]">
            <dt>Создал</dt>
            <dd className="text-ink">
              {task.createdByChat ? `чат ${task.createdByChat}` : (task.createdByName ?? '—')} ·{' '}
              {dateTime(task.createdAt)}
            </dd>
            <dt>Изменил</dt>
            <dd className="text-ink">
              {task.updatedByChat ? `чат ${task.updatedByChat}` : (task.updatedByName ?? '—')} ·{' '}
              {dateTime(task.updatedAt)}
            </dd>
            <dt>Номер</dt>
            <dd className="[overflow-wrap:anywhere] text-ink">
              {task.id}
              {task.ref ? ` (прежняя доска: ${task.ref})` : ''} · версия {task.version}
            </dd>
          </dl>
        </div>
      ) : null}
    </Sheet>
  );
}
